import React, { useEffect, useRef, useState } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { EffectFade, Autoplay } from "swiper/modules"
import { GatsbyImage } from "gatsby-plugin-image"
import { useStaticQuery, graphql } from "gatsby"
import { Link } from "gatsby"
import { useLocation } from "@reach/router"
import useAccordionHeight from "./useAccordionHeight"
import "swiper/css/effect-fade"
import "swiper/css"
export default function Header() {
    const mobileMenuRef = useRef(null)
    const { content } = useStaticQuery(graphql`
        query HeaderQuery {
            content: datoCmsHeader {
                projectCarousel {
                    gatsbyImageData
                }
                processCarousel {
                    title
                    content
                    image {
                        gatsbyImageData
                    }
                }
                designCarousel {
                    title
                    content
                    image {
                        gatsbyImageData
                    }
                }
            }
        }
    `)
    const location = useLocation()
    const [isScaled, setIsScaled] = useState(false)
    const [isLight, setIsLight] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [mobileMenuOverflow, setMobileMenuOverflow] = useState("hidden")
    const { height: mobileMenuHeight } = useAccordionHeight(mobileMenuRef, menuOpen)
    useEffect(() => {
        if (!menuOpen) {
            setMobileMenuOverflow("hidden")
            return
        }
        setMobileMenuOverflow("hidden")
        const timer = setTimeout(() => setMobileMenuOverflow("visible"), 500)
        return () => clearTimeout(timer)
    }, [menuOpen])
    useEffect(() => {
        let lastScrollY = window.scrollY
        const handleScroll = () => {
            const currentScrollY = window.scrollY
            const isMobile = window.innerWidth < 768
            setIsScaled(currentScrollY >= 200)
            if (isMobile) {
                setHidden(false)
                lastScrollY = currentScrollY
                return
            }
            if (currentScrollY > 800 && currentScrollY > lastScrollY) {
                setHidden(true)
            } else {
                setHidden(false)
            }
            lastScrollY = currentScrollY
        }
        window.addEventListener("scroll", handleScroll)
        window.addEventListener("resize", handleScroll)
        handleScroll()
        return () => {
            window.removeEventListener("scroll", handleScroll)
            window.removeEventListener("resize", handleScroll)
        }
    }, [])
    useEffect(() => {
        const hero = document.querySelector(".hero--banner")
        setIsLight(!!hero)
        setMenuOpen(false)
        setHidden(false)
    }, [location.pathname])

    return (
        <header className={`masthead ${isLight ? "is-light " : ""}${isScaled ? "scaled " : ""}${hidden ? "invisible" : ""}`}>
            <div className='flex ml40 mr40 m-ml20 m-mr20 mt20'>
                <div className='w-50 m-flex-1 mba'>
                    <div className='p15 m-p10 corner-box inner b1 pos-rel'>
                        <Link className='link' to='/'>
                            <div className='logo--main' />
                        </Link>
                    </div>
                    <div ref={mobileMenuRef} className={`mobile-menu${menuOpen ? " active" : ""}`} style={{ height: `${mobileMenuHeight}px`, overflow: mobileMenuOverflow }}>
                        <div className='pb10'>
                            <MobileMenu />
                        </div>
                    </div>
                </div>
                <div className='w-50 mba menu inner m-hide'>
                    <Menu content={content} />
                </div>
                <button className='mobile--button corner-box b1 m-show mln1 p15 m-p10'
                  onClick={() => {
                    setMenuOpen(prev => {
                      const next = !prev
                      document.body.classList.toggle("menu-open", next)
                      return next
                    })
                  }}
                  aria-label='Menu'
                >
                  {!menuOpen ? "Menu" : "Close"}
                </button>
            </div>
        </header>
    )
}

const Menu = ({content}) => {
    return (
        <div className='flex gap-0 p-sans f-15 lh-n'>
            <AboutLink />
            {( false ? <ProjectLink carousel={content.projectCarousel} />:'')}
            <ProcessLink carousel={content.processCarousel} />
            <DesignLink carousel={content.designCarousel} />
            {( false ?<InsightsLink />:'')}
            <Link className='menu-button corner-box' to='/contact'>Contact</Link>
        </div>
    )
}

const InsightsLink = () => {
    return (
        <div className='menu-button menu-parent corner-box pos-rel'>
            <Link  to='/insights' className='bg-image'/>
            <p className='default'>Insights</p>
            <div className='dropdown--menu flex flex-col h3'>
                <Link className='menu-button corner-box' to='/insights'>News</Link>
                <Link className='menu-button corner-box' to='/resources'>Resources</Link>
            </div>
        </div>
    )
}

const AboutLink = () => {
    return (
        <div className='menu-button menu-parent corner-box pos-rel'>
            <p className='default'>About</p>
            <div className='dropdown--menu flex flex-col h3'>
                <Link className='menu-button corner-box' to='/why'>Why Tekt</Link>
                <Link className='menu-button corner-box' to='/about'>About Us</Link>
                <Link className='menu-button corner-box' to='/people'>Work With Us</Link>
            </div>
        </div>
    )
}

const DesignLink = ({carousel}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [swiperRef, setSwiperRef] = useState(null)
    return (
        <div className='menu-button menu-parent corner-box pos-rel'>
            <Link  to='/design' className='bg-image'/>
            <p className='default'>Design</p>
            <div className='dropdown--menu flex flex-col'>
                <Link  to='/design'>
                    <div className='pos-rel bg-pale'>
                        <Swiper className='dropdown-carousel' slidesPerView={1} loop speed={650} effect='fade' autoplay={{ delay: 3000, disableOnInteraction: false }} fadeEffect={{ crossFade: true }} modules={[EffectFade, Autoplay]} onSwiper={setSwiperRef} onSlideChange={swiper => setActiveIndex(swiper.realIndex)}>
                            {carousel?.map((slide, i) => (
                                <SwiperSlide className='flex flex-col' key={i}>
                                    <div className='pos-rel pointer'>
                                        <div className='ratio-7-8 w-100 bg-grey pos-rel'>
                                            {slide.image ? <GatsbyImage image={slide.image?.gatsbyImageData} alt='Project' className='bg-image pointer' /> : ""}
                                        </div>
                                        <div className='project-dots'>
                                            {carousel?.map((_, i) => (
                                                <button key={i} className={`project-dot${i === activeIndex ? " active" : ""}`} onClick={() => swiperRef?.slideToLoop(i)} />
                                            ))}
                                        </div>
                                    </div>
                                    <div className='corner-box bg-pale p10 flex flex-col gap-10 h-100'>
                                        <p className='f-18'>{slide.title}</p>
                                        <div className='herbik f-15 mb20'>{slide.content}</div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                   </div>
                </Link>
            </div>
        </div>
    )
}

const ProcessLink = ({carousel}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [swiperRef, setSwiperRef] = useState(null)
    return (
        <div className='menu-button menu-parent corner-box pos-rel'>
            <Link  to='/process' className='bg-image'/>
            <p className='default'>Our Process</p>
            <div className='dropdown--menu flex flex-col'>
                <Link  to='/process'>
                    <div className='pos-rel bg-pale'>
                        <Swiper slidesPerView={1} loop speed={650} effect='fade' autoplay={{ delay: 3000, disableOnInteraction: false }} fadeEffect={{ crossFade: true }} modules={[EffectFade, Autoplay]} onSwiper={setSwiperRef} onSlideChange={swiper => setActiveIndex(swiper.realIndex)}>
                            {carousel?.map((slide, i) => (
                                <SwiperSlide className='flex flex-col' key={i}>
                                    <div className='pos-rel pointer'>
                                        <div className='ratio-7-8 w-100 bg-grey pos-rel'>
                                            {slide.image ? <GatsbyImage image={slide.image?.gatsbyImageData} alt='Project' className='bg-image pointer' /> : ""}
                                        </div>
                                        <div className='project-dots'>
                                            {carousel?.map((_, i) => (
                                                <button key={i} className={`project-dot${i === activeIndex ? " active" : ""}`} onClick={() => swiperRef?.slideToLoop(i)} />
                                            ))}
                                        </div>
                                    </div>
                                    <div className='corner-box bg-pale p10 flex flex-col gap-10 h-100'>
                                        <p className='f-18'>{slide.title}</p>
                                        <div className='herbik f-15 mb20'>{slide.content}</div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                   </div>
                </Link>
            </div>
        </div>
    )
}


const ProjectLink = ({carousel}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [swiperRef, setSwiperRef] = useState(null)
    return (
        <div className='menu-button menu-parent corner-box pos-rel'>
            <Link  to='/projects' className='bg-image'/>
            <p className='default'>Projects</p>
            <div className='dropdown--menu flex flex-col'>
                <Link  to='/projects'>
                    <div className='ratio-7-8 bg-grey pos-rel'>
                        <Swiper slidesPerView={1} allowTouchMove={false} loop speed={650} effect='fade' autoplay={{ delay: 3000, disableOnInteraction: false }} fadeEffect={{ crossFade: true }} modules={[EffectFade, Autoplay]} onSwiper={setSwiperRef} onSlideChange={swiper => setActiveIndex(swiper.realIndex)} className='bg-image pointer'>
                            {carousel?.map((image, i) => (
                                <SwiperSlide key={i}>
                                    {image ? <GatsbyImage image={image?.gatsbyImageData} alt='Project' className='bg-image' /> : ""}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <div className='project-dots'>
                            {carousel?.map((_, i) => (
                                <button key={i} className={`project-dot${i === activeIndex ? " active" : ""}`} onClick={() => swiperRef?.slideToLoop(i)} />
                            ))}
                        </div>
                    </div>
                </Link>
            </div>
        </div>
    )
}

const MOBILE_NAV = [
    { label: "About", to: "/about", children: [
        { label: "Why Tekt?", to: "/why" },
        { label: "About Us", to: "/about" },
        { label: "Work With Us", to: "/people" },
        { label: "FAQs", to: "/faqs" },
    ]},
    //{ label: "Projects", to: "/projects" },
    { label: "Our Process", to: "/process" },
    { label: "Design", to: "/design" },
    //{ label: "Insights", to: "/insights", children: [
    //    { label: "News", to: "/insights" },
    //    { label: "Resources", to: "/resources" },
    //]},
    { label: "Contact", to: "/contact" },
]

const MobileMenu = () => {
    const [openIndex, setOpenIndex] = useState(null)
    return (
        <div className='flex flex-col'>
            {MOBILE_NAV.map((item, i) => (
                <MobileNavItem
                    key={item.label}
                    item={item}
                    first={i === 0}
                    isOpen={openIndex === i}
                    onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                />
            ))}
        </div>
    )
}

const MobileNavItem = ({ item, first, isOpen, onToggle }) => {
    const wrapperCls = `mobile-nav-item corner-box${first ? "" : " mtn1"}`
    const submenuRef = useRef(null)
    const { height, overflow } = useAccordionHeight(submenuRef, Boolean(isOpen && item.children))
    if (!item.children) {
        return (
            <Link className={`${wrapperCls} mobile-nav-label h3 flex align-start`} to={item.to}>
                <span>{item.label}</span>
            </Link>
        )
    }
    return (
        <div className={wrapperCls}>
            <button type='button' className='mobile-nav-label h3 flex align-start space-between' onClick={onToggle} aria-expanded={isOpen}>
                <span>{item.label}</span>
                <ChevronArrow active={isOpen} />
            </button>
            <div ref={submenuRef} className='mobile-submenu' style={{ height: `${height}px`, overflow }}>
                <div className='mobile-submenu-inner flex flex-col'>
                    {item.children.map(child => (
                        <Link key={child.label} className='mobile-subitem h3' to={child.to}>
                            {child.label}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    )
}

const ChevronArrow = ({ active }) => (
    <svg className={`form-arrow${active ? " active" : ""}`} width='12' height='9' viewBox='0 0 18 13' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M17.0449 0.50016L8.99952 12.1212L0.954104 0.50016H17.0449Z' fill='currentColor' stroke='currentColor' />
    </svg>
)